// Seccion Viajes (rediseño, docs/PLAN-REDISENO-PORTAL.md §6.4): la bandeja
// de la planta filtrada (Requests, una por habilitacion) y la vista "Carga
// por franja" de esa planta (SlotLoad, movida del Inicio por decision 2 de
// Federico). La vista viaja en la URL (`?vista=carga`) para que el KPI y el
// aviso del Inicio puedan apuntarle. Con "Todas" se pide elegir planta.
//
// La carga sigue siendo la lectura multiplanta GET /provider/slot-load,
// filtrada aca por la planta: ningun cambio de API.
import { useCallback, useEffect, useId, useState } from "react";
import type { ExpiriesPort } from "../expiries/index.js";
import type { FleetPort } from "../fleet/port.js";
import { Segmented } from "../components/ui/segmented.js";
import { SlotLoad } from "../home/SlotLoad.js";
import { loadsInScope } from "../logic/home.js";
import { accessesForSite } from "../logic/navigation.js";
import type { MultiSitePort } from "../multi-site/port.js";
import { usePolled } from "../multi-site/usePolled.js";
import { SiteScoped } from "../portal/SiteScoped.js";
import { systemClock, type Clock } from "../requests/clock.js";
import type { SectionContext } from "../shell/types.js";
import type { SiteAccess } from "../sites/port.js";
import { TripRouteSheet } from "../trips/RouteSheet.js";
import type { RequestsPort } from "./port.js";
import { Requests } from "./Requests.js";

/** La carga cambia por asignacion, no por reloj: un minuto alcanza (como en el Inicio). */
const LOAD_POLL_MS = 60_000;
const TICK_MS = 1_000;
export const LOAD_VIEW = "carga";

interface Props {
  context: SectionContext;
  requestsPort: RequestsPort;
  fleetPort: FleetPort & ExpiriesPort;
  multiSitePort: MultiSitePort;
  clock?: Clock;
}

const VIEWS = [
  { value: null, label: "Bandeja" },
  { value: LOAD_VIEW, label: "Carga por franja" },
] as const;
type View = (typeof VIEWS)[number]["value"];

function LoadView({ context, port, clock, site }: { context: SectionContext; port: MultiSitePort; clock: Clock; site: SiteAccess }): React.JSX.Element {
  const id = useId();
  const { session } = context;
  const read = useCallback(() => port.slotLoad(session), [port, session]);
  const load = usePolled(read, LOAD_POLL_MS, clock);
  const [now, setNow] = useState(() => clock.now());
  useEffect(() => {
    setNow(clock.now());
    return clock.every(TICK_MS, () => setNow(clock.now()));
  }, [clock]);
  const loads = load.state.estado === "listo" ? loadsInScope(load.state.data.sites, [site]) : [];
  return (
    <>
      <div>
        <p className="m-0 text-eyebrow font-semibold uppercase tracking-eyebrow text-acero-texto">{site.name}</p>
        <h1 className="m-0 text-xl font-semibold leading-titulo tracking-titulo text-blanco">Carga por franja</h1>
        <p className="m-0 text-xs text-texto-suave">Viajes que salen en cada franja de 30 minutos contra vehículos sin viaje asignado. Se actualiza sola.</p>
      </div>
      <SlotLoad id={id} state={load.state} loads={loads} now={now} onRetry={load.refresh} />
    </>
  );
}

export function TripsSection({ context, requestsPort, fleetPort, multiSitePort, clock = systemClock }: Props): React.JSX.Element {
  const accesses = accessesForSite(context.sites, context.siteId);
  const first = accesses[0];
  if (first === undefined) {
    return (
      <SiteScoped context={context} purpose="ver sus ofertas y viajes">
        {() => null}
      </SiteScoped>
    );
  }
  const view = context.view === LOAD_VIEW ? LOAD_VIEW : null;
  return (
    <>
      <Segmented<View> label="Qué mostrar" value={view} options={VIEWS} onChange={(next) => context.selectView(next)} />
      {view === LOAD_VIEW ? (
        <LoadView context={context} port={multiSitePort} clock={clock} site={first} />
      ) : (
        accesses.map((site) => (
          // La key remonta la vista al cambiar de planta: nada de la anterior queda en pantalla.
          <div key={`${site.siteId}:${site.providerId}`} className="contents">
            <Requests
              session={context.session}
              port={requestsPort}
              fleetPort={fleetPort}
              site={site}
              clock={clock}
              // PP-41: el viaje asignado abre su hoja de ruta (imprimir y compartir).
              assignedDetail={({ trip, vehicles, drivers }) => (
                <TripRouteSheet session={context.session} port={requestsPort} site={site} trip={trip} vehicles={vehicles} drivers={drivers} />
              )}
            />
          </div>
        ))
      )}
    </>
  );
}
